<template>
    <div
        class="flex-col h-full bg-no-repeat bg-center bg-64"
        :style="!selectedConversation ? `background-image: url(/img/activix-dark-mono.png);` : ''"
    >
        <conversation-info
            class="py-2 px-3 rounded-md shadow-xl bg-white z-10 md:pr-2 | xl:h-12 xl:py-0"
            v-if="selectedConversation && selectedConversation.id"
        />

        <section
            class="flex-1 overflow-x-hidden overflow-y-auto px-4 -mx-4 | lg:px-0 lg:mx-0"
            :class="{ '-mb-8': !showMessageInput }"
            ref="messageList"
            v-if="selectedConversation && selectedConversation.id"
        >
            <infinite-loading
                direction="top"
                :distance="300"
                ref="infiniteLoading"
                @infinite="onInfinite"
            >
                <template #spinner>
                    <div class="flex justify-center px-4 pb-0 pt-8">
                        <nio-spinner :size="50" />
                    </div>
                </template>
                <template #no-results>
                    <span />
                </template>
                <template #no-more>
                    <span />
                </template>
            </infinite-loading>

            <div :class="{ 'pb-4': showMessageInput }" ref="messageListContent">
                <conversation-message-list-group
                    :inactive="selectedConversation.id && !selectedConversation.active"
                    :messages="groupMessages"
                    :key="date"
                    v-for="(groupMessages, date) in messageGroups"
                />
            </div>

            <!-- Archive -->
            <conversation-card
                class="my-8"
                :description="$t('chat.messageList.restoreBeforeInteract')"
                icon="light/archive"
                :title="$t('chat.messageList.conversationArchived')"
                v-if="isArchived"
            >
                <nio-button icon="regular/undo" @click="changeSelectedConversationActivation(true)">
                    {{ $t('chat.messageList.restore') }}
                </nio-button>
            </conversation-card>


            <conversation-card
                class="my-8"
                :description="$t('chat.messageList.changePhoneBeforeInteract')"
                icon="light/road-sign-banned"
                :title="$t('chat.messageList.isUnreachable', { number: formatPhone(selectedConversation.contact_phone.number) })"
                v-if="isUnreachable"
            />

            <!-- Number Inactive or deleted -->
            <conversation-card
                class="my-8 whitespace-pre-line"
                icon="light/road-sign-banned"
                max-width-class="w-120 max-w-120"
                :title="$t('chat.messageList.numberUnavailablePolestar')"
                v-if="providerPhoneUnavailablePolestar"
            >
                <p class="mb-4" v-html="$t('chat.messageList.useNewNumberPolestar')" />
                <div class="flex flex-col items-center w-full">
                    <div class="w-120 h-64 px-4 mb-4">
                        <iframe
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen="allowfullscreen"
                            frameborder="0"
                            height="100%"
                            show-info="0"
                            src="https://www.youtube-nocookie.com/embed/OtSSJjXHaao?showinfo=0"
                            title="Text Messaging Update With Activix"
                            width="100%"
                        />
                    </div>
                    <nio-button
                        @click="triggerChangeSelectedConversationProviderPhone(true)"
                        v-show="selectedConversation.account.availableProviderPhones.length"
                    >
                        {{ $t('chat.messageList.assignNewNumberWithTemplatePolestar') }}
                    </nio-button>
                </div>
            </conversation-card>

            <!-- Number Inactive or deleted -->
            <conversation-card
                class="my-8 whitespace-pre-line"
                icon="light/road-sign-banned"
                max-width-class="w-120 max-w-120"
                :title="$t('chat.messageList.numberUnavailable')"
                v-if="providerPhoneUnavailable && !providerPhoneUnavailablePolestar"
            >
                <p class="mb-4" v-html="$t('chat.messageList.useNewNumber', { number: formatPhone(selectedConversation.provider_phone.number)})" />
                <div class="flex flex-col items-center w-full">
                    <nio-button
                        @click="triggerChangeSelectedConversationProviderPhone(false)"
                        v-show="selectedConversation.account.availableProviderPhones.length"
                    >
                        {{ $t('chat.messageList.assignNewNumberWithTemplate') }}
                    </nio-button>
                </div>
            </conversation-card>

            <!-- User Unsubscribed -->
            <conversation-card
                class="my-8"
                :description="$t('chat.messageList.userMustResubscribe', { number: formatPhone(selectedConversation.provider_phone.number) })"
                icon="light/road-sign-banned"
                :title="$t('chat.messageList.userUnsubscribed')"
                v-if="isUnsubscribed"
            />

            <!-- User Consent is not valid -->
            <conversation-card
                class="my-8"
                :description="$t('chat.messageList.userConsentMoreInfo', { date: formatDate(selectedConversation.contact_phone.contact.consent_limit_date) })"
                icon="light/road-sign-banned"
                :title="$t('chat.messageList.consentExpiration')"
                v-if="consentExpired"
            />

            <!-- Not in conversation -->
            <conversation-card
                class="my-8"
                :description="$t('chat.messageList.notInConversation')"
                icon="light/view-1"
                :title="$t('chat.messageList.readOnly')"
                v-if="isNotInConversation"
            />
        </section>

        <conversation-message-input :conversation="selectedConversation" v-if="showMessageInput" />
    </div>
</template>

<script>
    import {
        mapState, mapGetters, mapActions, mapMutations,
    } from 'vuex';
    import orderBy from 'lodash/orderBy.js';
    import groupBy from 'lodash/groupBy.js';

    import ConversationCard from '@/components/Chat/ConversationCard.vue';
    import ConversationInfo from '@/components/Chat/ConversationInfo.vue';
    import ConversationMessageInput from '@/components/Chat/ConversationMessageInput.vue';
    import ConversationMessageListGroup from '@/views/Chat/ConversationMessageListGroup.vue';
    import InfiniteLoading from 'vue-infinite-loading';

    export default {
        components: {
            ConversationCard,
            ConversationInfo,
            ConversationMessageInput,
            ConversationMessageListGroup,
            InfiniteLoading,
        },

        data() {
            return {
                perPage: 25, // Should be equal to the number of messages loaded in a conversation in backend to avoid problems with infinite loading
                total: -1, // Total number of items
                isInfiniteScrolling: false, // True when infinite scrolling is taking place
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapGetters([
                'partner',
            ]),

            ...mapGetters('chat', [
                'selectedConversation',
            ]),

            userInConversation() {
                if (empty(this.selectedConversation.users)) {
                    return true;
                }

                const user = this.selectedConversation.users.filter(tmpUser => tmpUser.id == this.authUser.id);

                return !empty(user);
            },

            messages() {
                if (!this.selectedConversation || !this.selectedConversation.messages.length) {
                    return [];
                }

                return orderBy(this.selectedConversation.messages, 'created_at', 'asc');
            },

            availablePhones() {
                return this.selectedConversation.contact_phone.contact.phones?.map(phone => ({
                    id: phone.id,
                    number: this.formatPhone(phone.number, true),
                }));
            },

            numberChanges() {
                return this.selectedConversation.audits.filter(audit => {
                    return audit.new_values.contact_phone_id;
                }).map(audit => {
                    return {
                        created_at: audit.created_at,
                        new_number: this.availablePhones
                            .find(phone => phone.id == audit.new_values.contact_phone_id)?.number,
                        old_number: this.availablePhones
                            .find(phone => phone.id == audit.old_values.contact_phone_id)?.number,
                        type: 'numberChange',
                    };
                });
            },

            messageGroups() {
                const events = orderBy([...this.messages, ...this.numberChanges], 'created_at', 'asc');
                return groupBy(events, message => {
                    return moment(message.created_at).format('YYYY-MM-DD');
                });
            },

            hasSelectedConversation() {
                return this.selectedConversation && this.selectedConversation.id;
            },

            isArchived() {
                return this.hasSelectedConversation && !this.selectedConversation.active && this.userInConversation;
            },

            providerPhoneUnavailablePolestar() {
                // Only for polestar for now (organization_id = 1706)
                if (this.authUser.organization_id != 1706 ||
                    !this.selectedConversation.provider_phone.replacement_provider_phone_id
                ) {
                    return false;
                }

                return this.hasSelectedConversation &&
                    (
                        !this.selectedConversation.provider_phone || !this.selectedConversation.provider_phone.active
                    ) &&
                    this.userInConversation;
            },

            providerPhoneUnavailable() {
                return this.hasSelectedConversation &&
                    (
                        !this.selectedConversation.provider_phone || !this.selectedConversation.provider_phone.active
                    ) &&
                    this.userInConversation;
            },

            isUnsubscribed() {
                return this.hasSelectedConversation && !this.selectedConversation.contact_phone.contact.subscribed && this.userInConversation;
            },

            hasCaslFeature() {
                return this.$feature.isEnabled('casl_compliance', this.authUser);
            },

            consentExpired() {
                return this.hasCaslFeature && this.hasSelectedConversation && !this.selectedConversation.contact_phone.contact.consent && this.userInConversation;
            },

            isNotInConversation() {
                return this.hasSelectedConversation && !this.userInConversation;
            },

            lastMessageForCurrentPhone() {
                return this.selectedConversation
                    .messages
                    .filter((message) => {
                        return message.contact_phone_id === this.selectedConversation.contact_phone_id;
                    })[0];
            },

            lastMessageWasUndeliveredAndUnreachable() {
                return this.lastMessageForCurrentPhone && this.lastMessageForCurrentPhone.status === 'undelivered' && this.lastMessageForCurrentPhone.error_code === '30006';
            },

            isUnreachable() {
                return this.lastMessageForCurrentPhone &&
                    this.selectedConversation.contact_phone_id == this.lastMessageForCurrentPhone.contact_phone_id &&
                    this.lastMessageWasUndeliveredAndUnreachable;
            },

            showMessageInput() {
                return this.hasSelectedConversation &&
                    !this.isUnreachable &&
                    !this.isArchived &&
                    !this.providerPhoneUnavailable &&
                    !this.isUnsubscribed &&
                    !this.consentExpired &&
                    !this.isNotInConversation;
            },
        },

        watch: {
            '$route.params'(newValue, oldValue) {
                if (empty(newValue.id) || newValue.id == oldValue.id) {
                    return;
                }

                this.$nextTick(() => {
                    this.scrollBottom();
                    this.triggerMarkAllMessagesAsRead();
                });
            },

            selectedConversation(newValue, oldValue) {
                this.$nextTick(() => {
                    if (typeof oldValue === 'undefined' &&
                        newValue &&
                        newValue.messages &&
                        newValue.messages.length < this.perPage
                    ) {
                        this.$refs.infiniteLoading.stateChanger.complete();
                    }

                    if (typeof oldValue === 'undefined' ||
                        typeof newValue === 'undefined' ||
                        newValue.id == oldValue.id
                    ) {
                        return;
                    }

                    this.isInfiniteScrolling = false;
                    this.total = -1;

                    if (newValue && newValue.messages) {
                        if (newValue.messages.length < this.perPage) {
                            this.$refs.infiniteLoading.stateChanger.complete();
                        } else {
                            // Reset infinite loading
                            this.$refs.infiniteLoading.stateChanger.reset();
                        }
                    }
                });
            },

            messages() {
                this.$nextTick(() => {
                    if (this.isInfiniteScrolling) {
                        this.isInfiniteScrolling = false;
                        return;
                    }

                    this.scrollBottom();
                });
            },

            'selectedConversation.active'(newValue) {
                if (newValue) {
                    return;
                }

                this.$nextTick(() => {
                    this.scrollBottom();
                });
            },
        },

        methods: {
            ...mapActions('chat', [
                'changeSelectedConversationActivation',
                'changeSelectedConversationProviderPhone',
            ]),

            ...mapMutations('chat', [
                'addMessage',
                'markAllMessagesAsRead',
            ]),

            triggerChangeSelectedConversationProviderPhone(withTemplate) {
                if (withTemplate) {
                    localStorage[`conversation_${this.selectedConversation.id}_message`] = this.$t('chat.messageList.numberChangeTemplate', {
                        clientFullName: this.selectedConversation.contact_phone.contact.first_name,
                        userFullName: this.authUser.first_name,
                        dealerName: this.authUser.account.name,
                        originalNumer: this.formatPhone(this.selectedConversation.provider_phone.number),
                    });
                }

                this.changeSelectedConversationProviderPhone();
            },

            triggerMarkAllMessagesAsRead() {
                if (!this.selectedConversation || !this.messages.length) {
                    return;
                }

                // Make sure there is an unread message
                const unreadMessage = this.messages.find(message => !message.read);

                if (empty(unreadMessage)) {
                    return;
                }

                this.markAllMessagesAsRead(this.selectedConversation.id);

                this.$axios.post('messages/read', {
                    conversation_id: this.selectedConversation.id,
                });
            },

            async getMessages(page = 1) {
                const response = await this.$axios.get('messages', {
                    params: {
                        page,
                        conversation_id: this.selectedConversation.id,
                        perPage: this.perPage,
                    },
                });

                const loaderHeight = this.$refs.infiniteLoading.$el.offsetHeight;
                const previousHeight = this.$refs.messageListContent.offsetHeight - this.$refs.messageList.scrollTop;

                this.total = response.data.meta.total;

                // Add messages to conversation
                response.data.data.forEach(message => {
                    this.addMessage({
                        message,
                        append: true,
                    });
                });

                this.$refs.infiniteLoading.stateChanger.loaded();

                this.$nextTick(() => {
                    this.$refs.messageList.scrollTop = this.$refs.messageListContent.offsetHeight - previousHeight - loaderHeight;
                });
            },

            onInfinite($state) {
                if (this.messages.length < this.total && this.total > this.perPage || this.total == -1) {
                    if (this.messages.length == 0) {
                        $state.complete();

                        return;
                    }

                    // Page to fetch
                    const page = Math.floor((this.messages.length / this.perPage) + 1);

                    // Avoid double infinite loading
                    if (!this.isInfiniteScrolling) {
                        this.isInfiniteScrolling = true;

                        this.getMessages(page, false);
                    } else {
                        this.$refs.infiniteLoading.stateChanger.loaded();
                    }
                } else {
                    $state.complete();
                }
            },

            scrollBottom() {
                if (this.$refs.messageList && this.$refs.messageListContent) {
                    this.$refs.messageList.scrollTop = this.$refs.messageListContent.offsetHeight;
                }
            },
        },

        mounted() {
            this.$nextTick(() => {
                this.scrollBottom();
            });
        },
    };
</script>
